// src/components/TaskCard.tsx

import React, { useState, useContext } from 'react';
import { Task, ApprovalStatus } from '../services/api/types';
import { apiService } from '../services/api';
import { predecessorMinStart, formatDayBR } from '../utils/taskSchedule';
import { isManagementRole, canApproveOvertime, normalizeRole } from '../../shared/lib/roles';
import { AuthContext } from '../contexts/AuthAdapter';
import { Button, IconButton, Badge, Input, Textarea } from '../../shared/components/ui';
import './TaskCard.css';

interface TaskCardProps {
  task: Task;
  onTaskComplete: (taskId: number) => void;
  onTaskDelete: (taskId: number) => void;
  onTaskUpdate: (taskId: number, updates: { name?: string; description?: string; task_date?: string; expected_hours?: number; completion_percentage?: number }) => void;
  onTaskApproved?: (taskId: number) => void;
  loading?: {
    completing?: boolean;
    deleting?: boolean;
    updating?: boolean;
  };
}

const APPROVAL_BADGE: Record<ApprovalStatus, { tone: 'neutral' | 'brand' | 'success' | 'danger'; label: string }> = {
  pending: { tone: 'neutral', label: 'Aprovação pendente' },
  approved_by_manager: { tone: 'brand', label: 'Aprovada (gerente)' },
  approved_by_director: { tone: 'success', label: 'Aprovada (diretor)' },
  rejected: { tone: 'danger', label: 'Horas rejeitadas' },
};

// Função auxiliar para formatar data de forma segura
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'Sem data';
  try {
    const date = new Date(dateStr.split('T')[0] + 'T12:00:00');
    if (isNaN(date.getTime())) return 'Sem data';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return 'Sem data';
  }
};

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onTaskComplete,
  onTaskDelete,
  onTaskUpdate,
  onTaskApproved,
  loading = {}
}) => {
  const { user } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>(task.approval_status || 'pending');
  const [editName, setEditName] = useState(task.name);
  const [editDescription, setEditDescription] = useState(task.description || '');
  const [editTaskDate, setEditTaskDate] = useState(task.task_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
  const [editExpectedHours, setEditExpectedHours] = useState(task.expected_hours?.toString() || '');
  const [editCompletionPercentage, setEditCompletionPercentage] = useState(task.completion_percentage?.toString() || '0');

  const handleSaveEdit = () => {
    if (editName.trim()) {
      if (predecessorMin && editTaskDate && editTaskDate < predecessorMin) {
        alert(`A data de início deve ser a partir de ${formatDayBR(predecessorMin)}, um dia após a conclusão da predecessora.`);
        return;
      }
      const updates: any = {
        name: editName.trim(),
        description: editDescription.trim(),
        task_date: editTaskDate
      };

      if (editExpectedHours.trim()) {
        updates.expected_hours = parseFloat(editExpectedHours);
      }

      const completionPercentage = parseInt(editCompletionPercentage);
      if (!isNaN(completionPercentage) && completionPercentage >= 0 && completionPercentage <= 100) {
        updates.completion_percentage = completionPercentage;
      }

      onTaskUpdate(task.id, updates);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(task.name);
    setEditDescription(task.description || '');
    setEditTaskDate(task.task_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
    setEditExpectedHours(task.expected_hours?.toString() || '');
    setEditCompletionPercentage(task.completion_percentage?.toString() || '0');
    setIsEditing(false);
  };

  const handleComplete = () => {
    onTaskComplete(task.id);
  };

  const isDirector = normalizeRole((user as any)?.globalRole || user?.role) === 'director';
  const canApprove = canApproveOvertime((user as any)?.globalRole || user?.role);

  const handleApprove = async (action: 'approve' | 'reject') => {
    const status: ApprovalStatus = action === 'reject'
      ? 'rejected'
      : isDirector
        ? 'approved_by_director'
        : 'approved_by_manager';
    setApproving(true);
    try {
      await apiService.tasks.approveTask(task.id, { approval_status: status });
      setApprovalStatus(status);
      onTaskApproved?.(task.id);
    } catch (e: any) {
      console.error('Error approving task:', e);
      alert(e.message || 'Erro ao registrar aprovação.');
    } finally {
      setApproving(false);
    }
  };

  const approvalBadge = APPROVAL_BADGE[approvalStatus];

  const canEdit = !!user;
  const canDelete = isManagementRole((user as any)?.globalRole || user?.role) || user?.id === task.created_by;
  const isDisabled = loading.completing || loading.deleting || loading.updating;
  const isCompleted = task.status === 'completed';

  // Vínculo de predecessora: nome, limite mínimo de início e bloqueio de conclusão
  const predecessorName = task.predecessor ? task.predecessor.name || task.predecessor.title : null;
  const predecessorPending = !!task.predecessor && task.predecessor.status !== 'completed';
  const predecessorMin = task.predecessor ? predecessorMinStart(task.predecessor) : null;

  // Calcular horas completadas
  const completedHours = task.expected_hours && task.completion_percentage
    ? ((task.expected_hours * task.completion_percentage) / 100).toFixed(1)
    : null;

  const progress = task.completion_percentage !== undefined
    ? Math.min(task.completion_percentage, 100)
    : 0;

  return (
    <div
      data-testid="task-card"
      className={`rounded-xl border bg-surface-1 p-6 shadow-xs transition-all duration-150 ${
        isCompleted ? 'border-success/30 bg-success/5' : 'border-border hover:border-border-strong hover:shadow-md'
      } ${isDisabled ? 'pointer-events-none opacity-60' : ''}`}
    >
      {isEditing ? (
        <div className="flex flex-col gap-4">
          <Input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Nome da tarefa"
            disabled={isDisabled}
            data-testid="task-edit-name"
          />
          <Textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Descrição (opcional)"
            rows={2}
            disabled={isDisabled}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label="Data"
              type="date"
              value={editTaskDate}
              min={predecessorMin ?? undefined}
              onChange={(e) => setEditTaskDate(e.target.value)}
              disabled={isDisabled}
            />
            <Input
              label="Horas Previstas"
              type="number"
              value={editExpectedHours}
              onChange={(e) => setEditExpectedHours(e.target.value)}
              placeholder="0"
              min="0"
              step="0.5"
              disabled={isDisabled}
            />
            <Input
              label="Conclusão %"
              type="number"
              value={editCompletionPercentage}
              onChange={(e) => setEditCompletionPercentage(e.target.value)}
              placeholder="0"
              min="0"
              max="100"
              disabled={isDisabled}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={handleCancelEdit}
              disabled={isDisabled}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveEdit}
              loading={loading.updating}
              disabled={isDisabled || !editName.trim()}
              data-testid="task-save-button"
            >
              {loading.updating ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Header com título e ações */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {isCompleted && <Badge tone="success" dot>Concluída</Badge>}
              {task.activity_type && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  style={{
                    backgroundColor: `${task.activity_type.color || '#6366f1'}1a`,
                    color: task.activity_type.color || '#6366f1',
                  }}
                  data-testid="task-activity-type-badge"
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: task.activity_type.color || '#6366f1' }} />
                  {task.activity_type.name}
                </span>
              )}
              <Badge tone={approvalBadge.tone} data-testid="task-approval-badge">{approvalBadge.label}</Badge>
              {predecessorName && (
                <Badge
                  tone={predecessorPending ? 'warning' : 'success'}
                  data-testid="task-predecessor-badge"
                  title={
                    predecessorPending
                      ? `Aguardando a conclusão de "${predecessorName}"`
                      : `Predecessora "${predecessorName}" concluída`
                  }
                >
                  Depende de: {predecessorName}
                </Badge>
              )}
              <h4 className="font-display text-base font-semibold text-foreground">{task.name}</h4>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {canEdit && !isCompleted && (
                <IconButton
                  aria-label="Editar tarefa"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  disabled={isDisabled}
                  data-testid="task-edit-button"
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  }
                />
              )}
              {canDelete && (
                <IconButton
                  aria-label="Excluir tarefa"
                  variant="danger"
                  size="sm"
                  onClick={() => onTaskDelete(task.id)}
                  disabled={isDisabled}
                  data-testid="task-delete-button"
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  }
                />
              )}
            </div>
          </div>

          {/* Descrição */}
          {task.description && (
            <p className="mt-2 text-sm text-foreground-muted">{task.description}</p>
          )}

          {/* Informações em grid */}
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Data</span>
              <span className="text-sm font-medium text-foreground tabular-nums">{formatDate(task.task_date)}</span>
            </div>
            {task.expected_hours && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Previsto</span>
                <span className="text-sm font-medium text-foreground tabular-nums">{task.expected_hours}h</span>
              </div>
            )}
            {task.completion_percentage !== undefined && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Progresso</span>
                <span className="text-sm font-medium text-foreground tabular-nums">{task.completion_percentage}%</span>
              </div>
            )}
            {completedHours && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Realizado</span>
                <span className="text-sm font-medium text-foreground tabular-nums">{completedHours}h</span>
              </div>
            )}
          </div>

          {/* Barra de progresso */}
          {task.completion_percentage !== undefined && (
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-surface-3">
              <div
                className={`h-full rounded-full transition-all duration-300 ${isCompleted ? 'bg-success' : 'bg-brand'}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Botão de completar (bloqueado enquanto a predecessora não concluir) */}
          {!isCompleted && (
            <>
              <Button
                variant="success"
                fullWidth
                className="mt-5"
                onClick={handleComplete}
                loading={loading.completing}
                disabled={isDisabled || predecessorPending}
                data-testid="task-complete-button"
              >
                {loading.completing ? 'Concluindo...' : 'Marcar como Concluída'}
              </Button>
              {predecessorPending && (
                <p className="mt-2 text-center text-xs text-warning" data-testid="task-predecessor-block-hint">
                  Aguardando a conclusão da predecessora "{predecessorName}"
                </p>
              )}
            </>
          )}

          {/* Aprovação gerencial das horas */}
          {canApprove && approvalStatus !== 'approved_by_director' && (
            <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
              <span className="mr-auto text-xs font-medium uppercase tracking-wide text-foreground-subtle">
                Aprovação de horas
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleApprove('reject')}
                loading={approving}
                disabled={approving}
                className="text-danger hover:bg-danger/10"
                data-testid="task-reject-button"
              >
                Rejeitar
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => handleApprove('approve')}
                loading={approving}
                disabled={approving}
                data-testid="task-approve-button"
              >
                {isDirector ? 'Aprovar (diretor)' : 'Aprovar horas'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskCard;
